/**
 * MSAL Service — Azure AD SSO using PublicClientApplication (PKCE flow)
 * No client secret needed. Same pattern as RCM.
 *
 * Azure App Registration config via env vars:
 *   REACT_APP_AZURE_CLIENT_ID
 *   REACT_APP_AZURE_TENANT_ID
 *   REACT_APP_AZURE_REDIRECT_URI
 */

import { PublicClientApplication } from '@azure/msal-browser';

const msalConfig = {
  auth: {
    clientId: process.env.REACT_APP_AZURE_CLIENT_ID,
    authority: `https://login.microsoftonline.com/${process.env.REACT_APP_AZURE_TENANT_ID}`,
    redirectUri: process.env.REACT_APP_AZURE_REDIRECT_URI || `${window.location.origin}/login`,
    postLogoutRedirectUri: window.location.origin,
    navigateToLoginRequestUrl: false,
  },
  cache: {
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: true,
  },
};

const loginRequest = {
  scopes: ['openid', 'profile', 'email', 'User.Read'],
};

// Singleton — created at module load (same as RCM pattern)
const msalInstance = new PublicClientApplication(msalConfig);

export async function initializeMsal() {
  await msalInstance.initialize();
  return msalInstance;
}

/**
 * Redirect to Azure AD login page
 */
export async function loginRedirect() {
  await initializeMsal();
  await msalInstance.loginRedirect(loginRequest);
}

/**
 * Handle the redirect response after Azure AD login
 * Returns user object if authenticated, null otherwise
 * Fetches actual role from backend (DocumentDB users collection)
 */
export async function handleRedirectResult() {
  await initializeMsal();

  try {
    const response = await msalInstance.handleRedirectPromise();
    console.log('[MSAL] handleRedirectPromise response:', response);
    console.log('[MSAL] Current URL:', window.location.href);
    console.log('[MSAL] Configured redirectUri:', msalConfig.auth.redirectUri);

    // If we got a response from redirect, use it
    if (response && response.account) {
      return await buildUserWithRole(response.account);
    }

    // Otherwise check if there's already an authenticated account
    const accounts = msalInstance.getAllAccounts();
    console.log('[MSAL] Existing accounts:', accounts.length);
    if (accounts.length > 0) {
      return await buildUserWithRole(accounts[0]);
    }
  } catch (error) {
    console.error('MSAL redirect error:', error);
  }

  return null;
}

/**
 * Get the current authenticated user (if any)
 */
export async function getCurrentSSOUser() {
  await initializeMsal();
  const accounts = msalInstance.getAllAccounts();

  if (accounts.length > 0) {
    try {
      await msalInstance.acquireTokenSilent({
        scopes: loginRequest.scopes,
        account: accounts[0],
      });
      return await buildUserWithRole(accounts[0]);
    } catch {
      return null;
    }
  }

  return null;
}

/**
 * Logout from Azure AD
 */
export async function logoutSSO() {
  await initializeMsal();
  await msalInstance.logoutRedirect();
}

/**
 * Build user object from MSAL account
 */
function buildUserFromAccount(account) {
  return {
    id: account.homeAccountId,
    email: account.username,
    full_name: account.name || account.username.split('@')[0],
    username: account.username.split('@')[0],
    role: 'admin', // Default — will be overridden by DB lookup
  };
}

/**
 * Build user with actual role from backend (DocumentDB users collection)
 * Pattern from revenue-assurance: POST /api/auth/lookup → returns { token, user }
 * If user not in DB → returns error (access denied)
 */
async function buildUserWithRole(account) {
  const user = buildUserFromAccount(account);
  const apiBase = process.env.REACT_APP_API_URL || '/api';

  try {
    // Call core-backend to lookup user in DocumentDB and get role + app JWT
    const response = await fetch(`${apiBase}/auth/lookup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: user.email }),
    });

    if (response.ok) {
      const data = await response.json();
      if (data.user) {
        user.role = data.user.role || 'admin';
        user.full_name = data.user.full_name || user.full_name;
        user.id = data.user.id || user.id;
      }
      // Store the app JWT for subsequent API calls
      if (data.token) {
        const { setAccessToken } = await import('./tokenService');
        setAccessToken(data.token);
        localStorage.setItem('token', data.token);
      }
      return user;
    }

    // User not found in DB — access denied
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || 'Access denied. Contact admin.');
  } catch (err) {
    if (err.message.includes('Access denied') || err.message.includes('not found')) {
      throw err; // Propagate access denied errors
    }
    // Backend unreachable — keep default role 'admin'
    console.warn('Could not fetch role from backend, using default: admin');
    return user;
  }
}
